// Audience Studio — wired to the real AI planner backend (see ./api.ts),
// the FastAPI service in the "DALM Signal Hub — AI Layer" reference repo
// (server.py: /v1/usecases, /v1/planner, /v1/budget-plan, /v1/domains,
// /v1/objectives). Domains and their signals come entirely from the backend
// now (GET /v1/domains, GET /v1/domains/{domain}/signals — see api.ts) —
// there's no local domain list here to double as a fallback. Objectives and
// the channel-mix/rationale/KPI content below stay local only as the offline
// fallback for /v1/budget-plan when that call fails, since GET /v1/objectives
// already returns the exact same 6 values.

// Domain is a plain string, not a fixed literal union — the real set of
// valid domains lives entirely server-side (GET /v1/domains) and has already
// been confirmed narrower than what used to be hardcoded here (a 400
// "unknown domain" error is how that was caught), so nothing here should
// pretend to know the full list at compile time.
export type Domain = string;
export type Objective = "Acquire" | "Activate" | "Engage" | "Grow" | "Retain" | "Win Back";
export type SignalCategory = "First-party" | "Behavioral" | "External" | "Predictive" | "Derived";

export const OBJECTIVES: { id: Objective; label: string; description: string }[] = [
  { id: "Acquire", label: "Acquire", description: "Find new customers" },
  { id: "Activate", label: "Activate", description: "Drive first meaningful action" },
  { id: "Engage", label: "Engage", description: "Increase usage & interaction" },
  { id: "Grow", label: "Grow", description: "Upsell / cross-sell / monetize" },
  { id: "Retain", label: "Retain", description: "Prevent churn & protect value" },
  { id: "Win Back", label: "Win Back", description: "Reactivate dormant customers" },
];

export const CHANNEL_MIXES: Record<Objective, { channel: string; pct: number }[]> = {
  Acquire: [
    { channel: "Meta Ads", pct: 45 },
    { channel: "Google Ads", pct: 35 },
    { channel: "WhatsApp / SMS", pct: 20 },
  ],
  Activate: [
    { channel: "WhatsApp / Push", pct: 45 },
    { channel: "Email / SMS", pct: 25 },
    { channel: "Retargeting Ads", pct: 30 },
  ],
  Engage: [
    { channel: "WhatsApp / Push", pct: 40 },
    { channel: "Email / SMS", pct: 30 },
    { channel: "Paid Retargeting", pct: 30 },
  ],
  Grow: [
    { channel: "WhatsApp / CRM", pct: 40 },
    { channel: "Email / SMS", pct: 25 },
    { channel: "Meta / Google", pct: 35 },
  ],
  Retain: [
    { channel: "CRM / Call Centre", pct: 35 },
    { channel: "WhatsApp / Push", pct: 40 },
    { channel: "Email / SMS", pct: 25 },
  ],
  "Win Back": [
    { channel: "WhatsApp / SMS", pct: 40 },
    { channel: "Meta / Google Retargeting", pct: 35 },
    { channel: "Email / Push", pct: 25 },
  ],
};

export const PLANNER_RATIONALE: Record<Objective, string> = {
  Acquire: "Prioritize scalable paid discovery, then use owned channels where matched first-party identities are available.",
  Activate: "Favor direct, lower-cost owned channels for known users, with retargeting used to recover non-responders.",
  Engage: "Use owned engagement channels first and paid retargeting selectively to reinforce behavior.",
  Grow: "Start with personalized owned/CRM actions for existing customers, then use paid channels for reinforcement and expansion.",
  Retain: "Retention is not primarily an ad problem. Prioritize CRM, service and direct engagement; use paid media only where it adds incremental reach.",
  "Win Back": "Blend direct reactivation with retargeting so dormant users can be reached across both owned and paid touchpoints.",
};

export const PRIMARY_KPI: Record<Objective, string> = {
  Acquire: "Qualified acquisitions / CAC",
  Activate: "Activation rate",
  Engage: "Incremental engagement",
  Grow: "Incremental revenue / conversion",
  Retain: "Retention / churn reduction",
  "Win Back": "Reactivation rate",
};

export const CURRENCIES = [
  { value: "₦", label: "NGN (₦)" },
  { value: "$", label: "USD ($)" },
  { value: "₵", label: "GHS (₵)" },
  { value: "KSh", label: "KES (KSh)" },
];

export const fmtCompact = (n: number): string => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(n % 1000000 ? 1 : 0)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return String(Math.round(n));
};

export const fmtMoney = (n: number): string => Math.round(n).toLocaleString("en-US");
